import { Role } from '../enums/role.enum';

/** Single Cognito user pool — all four portals share these token keys. */
export const AUTH_TOKEN_KEYS = {
  idToken: 'vantage_id_token',
  accessToken: 'vantage_access_token',
  refreshToken: 'vantage_refresh_token',
} as const;

/**
 * localStorage key holding the route a user was denied before being bounced to `/auth/login`.
 * Written by the route guards (`authGuard`, `subPortalAuthGuard`, `clientPortalAuthGuard`),
 * consumed once by `UserNavService.navigateAfterLogin()` after a successful sign-in, and cleared on logout.
 * Shared so a rename cannot silently strand the redirect — the writers and the reader are in
 * two different libs.
 */
export const REDIRECT_URL_KEY = 'vantage_redirect_url';

/**
 * localStorage key for the territory-switcher selection (the territory a cross-territory
 * user is "viewing as"). Persisted so the scope survives a refresh — mirrors the prototype's
 * session-stored `selectedTerritoryId`. Sent to the backend as the `X-Territory-Id` header by
 * `territoryContextInterceptor`; only ever narrows visibility under RLS.
 */
export const TERRITORY_CONTEXT_KEY = 'vantage_active_territory';

/**
 * Roles that may access the territory OIP portal (`/oip`).
 * `SUB_PROXY` is an OIP identity (lands on `/oip`) that *also* carries a cross-portal
 * grant into `/sub` — see `AuthStore.hasSubProxyGrant` + `subPortalAuthGuard`.
 */
export const OIP_ROLES = [
  Role.TerritoryAdmin,
  Role.TerritoryManager,
  Role.TerritoryWriter,
  Role.TerritoryReader,
  Role.SubProxy,
] as const;

/** Roles that may access the corporate/national portal (`/corp`). */
export const CORP_ROLES = [
  Role.SuperAdmin,
  Role.CorporateWriter,
] as const;

/** Roles that may access the Subcontractor portal (`/sub`). */
export const SUB_ROLES = [
  Role.SubAdmin,
  Role.SubWriter,
  Role.SubReader,
] as const;

/** Roles that may access the Client portal (`/client`). */
export const CLIENT_ROLES = [
  Role.ClientWriter,
  Role.ClientReader,
] as const;

/** All roles that require staff (Cognito) auth (territory + corporate). */
export const STAFF_ROLES = [...OIP_ROLES, ...CORP_ROLES] as const;

/** Home portal for a role — drives `roleLandingGuard`. */
export type Portal = 'oip' | 'corp' | 'sub' | 'client';
export function portalForRole(role: string): Portal | null {
  if ((CORP_ROLES as readonly string[]).includes(role)) return 'corp';
  if ((OIP_ROLES as readonly string[]).includes(role)) return 'oip';
  if ((SUB_ROLES as readonly string[]).includes(role)) return 'sub';
  if ((CLIENT_ROLES as readonly string[]).includes(role)) return 'client';
  return null;
}
